import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, ExternalLink } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { slugify } from '@/lib/seo';
import { DishImage } from '@/components/DishImage';

export const RecipeModal = ({ recipe, open, onClose }) => {
  if (!recipe) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-3xl max-h-[90vh] p-0 bg-white/70 backdrop-blur-2xl border border-white/40 shadow-xl backdrop-saturate-150 overflow-hidden"
        data-testid="recipe-modal"
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{recipe.name}</DialogTitle>
            <DialogDescription>Recipe details for {recipe.name}</DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-50 rounded-full p-2 bg-white/80 backdrop-blur-sm hover:bg-white transition-colors"
          data-testid="close-recipe-modal"
        >
          <X className="h-5 w-5 text-[#1C1917]" />
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          {/* Image / Gradient fallback */}
          <div className="relative h-64 md:h-full overflow-hidden">
            <DishImage
              src={recipe.image_url}
              alt={`${recipe.name} recipe`}
              fallbackInitial={recipe.name?.[0] || 'M'}
              className="w-full h-full"
              imgClassName="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-white tracking-tight mb-2">
                {recipe.name}
              </h2>
              <div className="flex gap-2 flex-wrap">
                {recipe.region && (
                  <span className="px-3 py-1 bg-[#F97316]/80 backdrop-blur-md rounded-full text-xs font-medium text-white border border-white/30">
                    {recipe.region}
                  </span>
                )}
                {(recipe.dietary_tags || []).map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-medium text-white border border-white/30"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="h-[500px]">
            <div className="p-8 font-['Outfit']">
              {/* Stats */}
              <div className="flex gap-6 mb-6 pb-6 border-b border-[#1C1917]/10">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#1C1917]/60 mb-1">Calories</p>
                  <p className="text-2xl font-semibold text-[#F97316]">{recipe.calories}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#1C1917]/60 mb-1">Prep Time</p>
                  <p className="text-2xl font-semibold text-[#2D4A22]">{recipe.prep_time}</p>
                </div>
              </div>

              {/* Ingredients */}
              <div className="mb-6">
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1C1917] mb-3">
                  Ingredients
                </h3>
                <ul className="space-y-2">
                  {recipe.ingredients.map((ingredient) => (
                    <li key={`ingredient-${ingredient}`} className="flex items-start gap-2 text-[#1C1917]/80">
                      <span className="text-[#F97316] mt-1.5">•</span>
                      <span className="leading-relaxed">{ingredient}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Instructions */}
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1C1917] mb-3">
                  Instructions
                </h3>
                <ol className="space-y-3">
                  {recipe.instructions.map((instruction, stepNum) => (
                    <li key={`step-${stepNum + 1}-${instruction.slice(0, 20)}`} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F97316] text-white text-sm flex items-center justify-center font-semibold">
                        {stepNum + 1}
                      </span>
                      <span className="leading-relaxed text-[#1C1917]/80">{instruction}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Share / permalink */}
              <div className="mt-6 pt-6 border-t border-[#1C1917]/10">
                <a
                  href={`/recipe/${slugify(recipe.name)}`}
                  data-testid="view-full-recipe-link"
                  className="inline-flex items-center gap-2 text-sm font-['Outfit'] font-medium text-[#F97316] hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  View full recipe page & share
                </a>
              </div>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};